import React, { useCallback, useEffect, useState } from "react";
import styles from '../instructorForm/InstructorForm.module.scss';
import PropTypes from "prop-types";
import ImageUploader from "../imageUploader/ImageUploader";
import TextField from "../textField/TextField";
import MultiSelectDropdown from "../multiSelectDropdown/MultiSelectDropdown";
import Button from "../button/Button";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber, getInitials } from "../../utils/stringUtils";

const ClinicalCoordinatorForm = ({
  formData,
  healthSystemList = [],
  clinicalSiteList = [],
  onDataChange,
  onSubmit,
  isEdit = false,
}) => {
  const [profilePicture, setProfilePicture] = useState({ initials: '', image: null });
  const [imageFile, setImageFile] = useState(null);
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isValidated, setIsValidated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setProfilePicture({
      initials: getInitials(formData?.firstName, formData?.lastName),
      image: formData?.profilePicture || null
    });
  }, [formData]);

  const validateForm = useCallback(() => {
    const validateFirstName = () => {
      if (!formData.firstName) return 'First Name is required';
      if (!/^[A-Za-z]{2,}/.test(formData.firstName)) return 'First Name must contain only letters and be at least 2 characters long.';
      return '';
    };
    const validateLastName = () => {
      if (!formData.lastName) return 'Last Name is required';
      if (!/^[A-Za-z]{2,}/.test(formData.lastName)) return 'Last Name must contain only letters and be at least 2 characters long.';
      return '';
    };
    const validateEmail = () => {
      if (!formData.email) return 'Email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Invalid email format';
      return '';
    };
    const validatePhone = () => {
      const cleaned = formData.phone.replace(/\D/g, '');
      if (!cleaned) return 'Phone number is required';
      if (!/^\d{10}$/.test(cleaned)) return 'Invalid phone number format. Must be 10 digits.';
      return '';
    };

    setIsValidated(true);
    const firstNameErr = validateFirstName();
    const lastNameErr = validateLastName();
    const emailErr = validateEmail();
    const phoneErr = validatePhone();

    setFirstNameError(firstNameErr);
    setLastNameError(lastNameErr);
    setEmailError(emailErr);
    setPhoneError(phoneErr);

    const errors = [
      { field: 'firstName', message: firstNameErr },
      { field: 'lastName', message: lastNameErr },
      { field: 'email', message: emailErr },
      { field: 'phone', message: phoneErr },
    ];
    const firstError = errors.find(e => e.message !== '');
    return firstError ? firstError.field : null;
  }, [formData]);

  useEffect(() => {
    if (isValidated) validateForm();
  }, [formData, isValidated, validateForm]);

  const scrollToFirstError = (field) => {
    const element = document.querySelector(`[data-error-field="${field}"]`);
    if (element) {
      window.scrollTo({ top: element.offsetTop - 72, behavior: 'smooth' });
    }
  };

  const handleSubmit = () => {
    const firstErrorField = validateForm();
    if (firstErrorField) {
      scrollToFirstError(firstErrorField);
      return;
    }
    onSubmit(imageFile);
  };

  return (
    <form
      className={styles.form}
      onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
    >
      <h2>Personal Information</h2>
      <ImageUploader
        {...profilePicture}
        onChange={(value) => setImageFile(value)}
      />
      <div className={styles.row}>
        <div className={styles.column50} data-error-field="firstName">
          <TextField
            label="First Name"
            value={formData.firstName}
            onChange={(value) => onDataChange({ firstName: value })}
            error={firstNameError}
          />
        </div>
        <div className={styles.column50} data-error-field="lastName">
          <TextField
            label="Last Name"
            value={formData.lastName}
            onChange={(value) => onDataChange({ lastName: value })}
            error={lastNameError}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column50} data-error-field="email">
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(value) => onDataChange({ email: value })}
            error={emailError}
          />
        </div>
        <div className={styles.column50} data-error-field="phone">
          <TextField
            label="Phone"
            value={formData.phone}
            onChange={(value) => onDataChange({ phone: formatPhoneNumber(value) })}
            error={phoneError}
          />
        </div>
      </div>
      <h2>Employment Details</h2>
      <div className={styles.row}>
        <div className={styles.column50}>
          <MultiSelectDropdown
            label="Health Systems"
            options={healthSystemList}
            selected={formData.healthSystemIds}
            onChange={(selected) => onDataChange({ healthSystemIds: selected })}
          />
        </div>
        <div className={styles.column50}>
          <MultiSelectDropdown
            label="Clinical Sites"
            options={clinicalSiteList}
            selected={formData.clinicalSiteIds}
            onChange={(selected) => onDataChange({ clinicalSiteIds: selected })}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column50}>
          <TextField
            label="Designation"
            value="Clinical Coordinator"
            onChange={() => {}}
            disabled
          />
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          label="Cancel"
          variant="white"
          size="medium"
          onClick={() => navigate('/clinical-coordinators')}
        />
        <Button
          label={isEdit ? 'Save Clinical Coordinator' : 'Create Clinical Coordinator'}
          variant="primary"
          size="medium"
          type="submit"
        />
      </div>
    </form>
  );
};

ClinicalCoordinatorForm.propTypes = {
  formData: PropTypes.object,
  healthSystemList: PropTypes.array.isRequired,
  clinicalSiteList: PropTypes.array.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
};

export default ClinicalCoordinatorForm;
